import { applicationService } from "services";
import { ApplicationStatusEnum, SortOrder } from "types";

export const getApplicantFullName = (basicDetails?: any): string => {
  if (!basicDetails) return "Not Provided";

  const name = [
    basicDetails.firstName,
    basicDetails.middleName,
    basicDetails.lastName,
  ];

  return name.filter(Boolean).join(" ");
};

export const sanitizeApplicationId = (id: string) => id.replace(/ap[^:]*:/, "");

export const formatAddress = (addressData: any) => {
  if (!addressData) {
    return "Not Provided";
  }

  const address = [
    addressData.street1,
    addressData.street2,
    addressData.city,
    addressData.state && addressData.postalCode
      ? `${addressData.state} ${addressData.postalCode}`
      : addressData.state || addressData.postalCode,
    addressData.country,
  ];

  return address.filter(Boolean).join(", ");
};

export const formatPhoneNumber = (phoneNumberData: any) => {
  if (!phoneNumberData) {
    return "Not Provided";
  }
  const formattedNumber = [
    phoneNumberData.countryCode || "",
    phoneNumberData.areaCode || "",
    phoneNumberData.number || "",
    phoneNumberData.extension || "",
  ].filter(Boolean);

  return formattedNumber.length > 0
    ? formattedNumber.join(" ")
    : "Not Provided";
};

export const getOptionLabel = (
  options: { value: string; label: string }[],
  value: string
) => options.find((o) => o.value === value)?.label ?? value;

export const updateApplicationWithCourseData = async (
  applications: any | any[]
) => {
  const isArray = Array.isArray(applications);
  const applicationsArray = Array.isArray(applications)
    ? applications
    : [applications];

  const result = await Promise.all(
    applicationsArray.map(async (application) => {
      try {
        const courseData = await applicationService.getCourseDetails(
          application.courseId
        );
        return { ...application, courseData };
      } catch {
        console.warn(
          "Could not fetch course information for application:",
          application.courseId
        );
        return { ...application, courseData: null };
      }
    })
  );

  return isArray ? result : result[0];
};

export const convertApplicationStatusToSteps = (
  status: ApplicationStatusEnum,
  totalSteps: number
) => {
  switch (status) {
    case ApplicationStatusEnum.SUBMITTED:
      return 0;
    case ApplicationStatusEnum.UNDER_REVIEW:
      return 1;
    case ApplicationStatusEnum.CONDITIONAL_OFFER_ISSUED:
      return 2;
    case ApplicationStatusEnum.UNCONDITIONAL_OFFER_ISSUED:
      return 3;
    case ApplicationStatusEnum.AWAITING_DOCUMENTS:
      return 4;
    case ApplicationStatusEnum.ACCEPTED:
      return totalSteps;
    case ApplicationStatusEnum.REJECTED:
      return totalSteps;
    case ApplicationStatusEnum.WITHDRAWN:
      return 7;
    default:
      return 1;
  }
};

// Filters
export const filterByStatus = (applications: any[], status: string) => {
  if (!status || status === "ALL") return applications;

  return applications.filter(
    (application) => application.status?.toLowerCase() === status.toLowerCase()
  );
};

export const filterBySearch = (applications: any[], query: string) => {
  if (!query) return applications;

  const sanitizedQuery = query.toLowerCase().trim();

  return applications.filter((application) => {
    const fullName = getApplicantFullName(
      application.personalInformation?.basicDetails
    ).toLowerCase();

    const applicantId = (application.owner || "").toLowerCase();

    return (
      fullName.includes(sanitizedQuery) || applicantId.includes(sanitizedQuery)
    );
  });
};

export const sortByCreatedAt = (
  applications: any[],
  order: SortOrder = SortOrder.DESCENDING
) => {
  return [...applications].sort((a, b) => {
    const aDate = new Date(a.createdAt ?? 0).getTime();
    const bDate = new Date(b.createdAt ?? 0).getTime();

    return order === SortOrder.ASCENDING ? aDate - bDate : bDate - aDate;
  });
};
